import { type ReactNode, useMemo, useState } from "react";
import {
  ArrowDownIcon,
  ArrowUpDownIcon,
  ArrowUpIcon,
  Building2Icon,
  TelescopeIcon,
  PlusIcon,
} from "lucide-react";
import { Link, useNavigate } from "react-router";

import { EmptyCellButton } from "./cells/EmptyCellButton";

import { BottleneckCell, CompanyLogoImage, Column, ErrorText, Row, Text } from "@/components";
import { DashboardLiquidityCell } from "@/components/dashboard/cells";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { type PortfolioMatrixJob } from "@/hooks/queries/portfolio/useGetPortfolioMatrix";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { JobBottleneck } from "@/types";

import { JobCell } from "./cells/JobCell";
import { CategoryCell } from "./cells/CategoryCell";
import { DifficultyCell } from "./cells/DifficultyCell";
import { HealthCell } from "./cells/HealthCell";
import { PriorityCell } from "./cells/PriorityCell";
import { PulseCell } from "./cells/PulseCell";
import { SalesCell } from "./cells/SalesCell";
import { StafferCell } from "./cells/StafferCell";

import { JobPriority } from "@/types";

// Priority sort: HIGH -> BACKBURNERED, unset (null) sorts last.
const PRIORITY_SORT_RANK: Record<JobPriority, number> = {
  [JobPriority.HIGH]: 0,
  [JobPriority.MEDIUM]: 1,
  [JobPriority.LOW]: 2,
  [JobPriority.BACKBURNERED]: 3,
};

const SKELETON_ROW_COUNT = 8;

const formatCount = (count: number) => (count === 0 ? "—" : count.toString());

const getColumnLabel = (column: PortfolioColumn, jobCount: number) =>
  column.key === "job-name"
    ? `${column.label} (${jobCount.toLocaleString()})`
    : column.label;

const formatRevenue = (val: number | null | undefined) => {
  if (val == null) return "—";
  return `$${val.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};

const formatDate = (iso: string | null) =>
  iso ? (dates.formatIsoDateForUser(iso) ?? "—") : "—";

// Sort key for dates: epoch ms, null when absent (sorts last).
const dateSortValue = (iso: string | null) =>
  iso ? new Date(iso).getTime() : null;

// Backend scalar score matches the Job Health card buckets.
const healthSortValue = (job: PortfolioMatrixJob) => job.score;

const prioritySortValue = (job: PortfolioMatrixJob) =>
  job.priority ? PRIORITY_SORT_RANK[job.priority] : null;

// Plain text cell — most columns just render formatted text.
const textCell = (value: string) => (
  <Text className="whitespace-nowrap text-sm" variant="secondary">
    {value}
  </Text>
);

// Dotted amber circle linking to job intake — shown when a deadline is missing.
const missingDeadlineWarning = (companyId: string, jobId: string) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <EmptyCellButton
        state="warning"
        asChild
        aria-label="No date added in job intake"
      >
        <Link
          to={`/companies/${companyId}/jobs/${jobId}/?t=job-intake`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <PlusIcon className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
        </Link>
      </EmptyCellButton>
    </TooltipTrigger>
    <TooltipContent>No date added in job intake</TooltipContent>
  </Tooltip>
);

// Deadline cell — shows the date, or a warning icon when missing that links to
// the job intake tab.
const deadlineCell = (iso: string | null, companyId: string, jobId: string) => {
  if (!iso) {
    return missingDeadlineWarning(companyId, jobId);
  }
  return textCell(formatDate(iso));
};

// Batch deadline cell — the velocity `batchDue` sub-score flags urgency with a
// background chip: 0 → critical (red), 0 < score < 60 → at risk (orange).
const batchDeadlineCell = (job: PortfolioMatrixJob) => {
  if (!job.batchDeadlineAt) {
    return missingDeadlineWarning(job.companyId, job.id);
  }

  const batchDue = job.healthSubScores?.velocity.batchDue ?? null;
  const label = formatDate(job.batchDeadlineAt);

  if (batchDue === null || batchDue >= 60) {
    return textCell(label);
  }

  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-sm font-medium",
        batchDue === 0
          ? "border-[#D14343] bg-[#D14343]/15 text-[#9B3030]"
          : "border-[#E67E22] bg-[#E67E22]/15 text-[#A85818]",
      )}
    >
      {label}
    </span>
  );
};

interface PortfolioColumn {
  key: string;
  label: string;
  render: (job: PortfolioMatrixJob) => ReactNode;
  /** Provide to make the column sortable. null values sort last. */
  sortValue?: (job: PortfolioMatrixJob) => string | number | null;
}

// Single source of truth for the health matrix: each column owns its label and
// a `render` (interactive cells return their component, static ones text).
// Matrix rows store bottleneck lowercase; the shared cell speaks the enum.
const MATRIX_BOTTLENECK_TO_ENUM: Record<
  NonNullable<PortfolioMatrixJob["bottleneck"]>,
  JobBottleneck
> = {
  customer: JobBottleneck.CUSTOMER,
  screening: JobBottleneck.SCREENING,
  sourcing: JobBottleneck.SOURCING,
};

const COLUMNS: PortfolioColumn[] = [
  {
    key: "job-name",
    label: "Job",
    sortValue: (job) => `${job.companyName} ${job.title}`.toLowerCase(),
    render: (job) => (
      <Row className="min-w-0 items-center gap-3">
        {job.companySmallLogoUrl || job.companyLogoUrl ? (
          <Row className="h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50">
            <CompanyLogoImage companyName={job.companyName} logoUrl={job.companyLogoUrl} smallLogoUrl={job.companySmallLogoUrl} fallback={<Building2Icon className="h-5 w-5 text-slate-400" />} />
          </Row>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to={`/companies/${job.companyId}?t=profile`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-dashed border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100"
              >
                <Building2Icon className="h-5 w-5 text-slate-400" aria-hidden />
              </Link>
            </TooltipTrigger>
            <TooltipContent>Add company logo</TooltipContent>
          </Tooltip>
        )}
        <Column className="min-w-0 gap-0.5">
          <Text className="truncate text-sm font-medium" variant="primary">
            {job.title}
          </Text>
          <Text className="truncate text-sm" variant="muted">
            {job.companyName}
          </Text>
        </Column>
      </Row>
    ),
  },
  {
    key: "liquidity",
    label: "Liquidity",
    render: (job) => (
      <DashboardLiquidityCell
        breakdown={job.liquidityBreakdown}
        liquidity={job.liquidity}
        showGuidance
      />
    ),
  },
  {
    key: "priority",
    label: "Priority",
    sortValue: prioritySortValue,
    render: (job) => <PriorityCell job={job} />,
  },
  {
    key: "score",
    label: "Health score",
    sortValue: healthSortValue,
    render: (job) => (
      <HealthCell
        companyId={job.companyId}
        companyName={job.companyName}
        health={job.health}
        jobId={job.id}
        jobTitle={job.title}
        reasons={job.healthReasons}
        score={job.score}
      />
    ),
  },
  {
    key: "sales",
    label: "Sales",
    render: (job) => <SalesCell job={job} />,
  },
  {
    key: "staffer",
    label: "Staffer",
    render: (job) => <StafferCell job={job} />,
  },
  {
    key: "last-staffer-action",
    label: "Last staffer action",
    sortValue: (job) => dateSortValue(job.lastStafferActionAt),
    render: (job) => textCell(formatDate(job.lastStafferActionAt)),
  },
  {
    key: "category",
    label: "Category",
    // Sort groups jobs by category; unset (null) sorts last.
    sortValue: (job) => job.category,
    render: (job) => <CategoryCell job={job} />,
  },
  {
    key: "difficulty",
    label: "Difficulty",
    // Sorts alphabetically by difficulty; unset (null) sorts last.
    sortValue: (job) => job.difficulty,
    render: (job) => <DifficultyCell job={job} />,
  },
  {
    key: "bottleneck",
    label: "Bottleneck",
    // Sorts alphabetically by bottleneck; unset (null) sorts last.
    sortValue: (job) => job.bottleneck,
    render: (job) => (
      <BottleneckCell
        bottleneck={
          job.bottleneck ? MATRIX_BOTTLENECK_TO_ENUM[job.bottleneck] : null
        }
        companyId={job.companyId}
        jobId={job.id}
      />
    ),
  },
  {
    key: "customer-deadline",
    label: "Customer deadline",
    sortValue: (job) => dateSortValue(job.customerDeadlineAt),
    render: (job) =>
      deadlineCell(job.customerDeadlineAt, job.companyId, job.id),
  },
  {
    key: "batch-deadline",
    label: "Batch deadline",
    sortValue: (job) => dateSortValue(job.batchDeadlineAt),
    render: (job) => batchDeadlineCell(job),
  },
  {
    key: "submissions",
    label: "Submissions",
    sortValue: (job) => job.pipelineCounts.submissions,
    render: (job) => textCell(formatCount(job.pipelineCounts.submissions)),
  },
  {
    key: "interviews",
    label: "Interviews",
    sortValue: (job) => job.pipelineCounts.interviews,
    render: (job) => textCell(formatCount(job.pipelineCounts.interviews)),
  },
  {
    key: "offers",
    label: "Offers",
    sortValue: (job) => job.pipelineCounts.offers,
    render: (job) => textCell(formatCount(job.pipelineCounts.offers)),
  },
  {
    key: "placements",
    label: "Placements",
    sortValue: (job) => job.pipelineCounts.placements,
    render: (job) => (
      <Column className="gap-0.5">
        <Text className="whitespace-nowrap text-sm" variant="secondary">
          {formatCount(job.pipelineCounts.placements)}
        </Text>
        {job.reqsTotal !== 0 && (
          <Text className="whitespace-nowrap text-xs text-slate-400">
            {job.reqsFilled.toString()}/{job.reqsTotal.toString()} reqs
          </Text>
        )}
      </Column>
    ),
  },
  {
    key: "last-submit",
    label: "Last submit",
    sortValue: (job) => dateSortValue(job.lastSubmitAt),
    render: (job) => textCell(formatDate(job.lastSubmitAt)),
  },
  {
    key: "revenue-on-table",
    label: "Rev on table",
    sortValue: (job) => job.revenueOnTable,
    render: (job) => textCell(formatRevenue(job.revenueOnTable)),
  },
  {
    key: "pulse",
    label: "Pulse",
    render: (job) => <PulseCell job={job} />,
  },
];

const renderSkeletonCell = (columnKey: string, rowIndex: number) => {
  if (columnKey === "job-name") {
    return (
      <Row className="min-w-[220px] items-center gap-3">
        <Skeleton className="h-10 w-10 shrink-0 rounded-md" />
        <Column className="min-w-0 gap-1.5">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-28" />
        </Column>
      </Row>
    );
  }

  if (columnKey === "score") {
    return (
      <Column className="gap-1.5">
        <Skeleton className="h-4 w-14" />
        <Skeleton className="h-2 w-20 rounded-full" />
      </Column>
    );
  }

  if (columnKey === "staffer") {
    return (
      <Row className="items-center">
        <Skeleton className="h-7 w-7 rounded-full" />
        <Skeleton className="-ml-2 h-7 w-7 rounded-full" />
      </Row>
    );
  }

  if (columnKey === "category") {
    return <Skeleton className="h-6 w-6 rounded" />;
  }

  if (
    ["priority", "difficulty", "bottleneck", "pulse", "sales"].includes(
      columnKey,
    )
  ) {
    return <Skeleton className="h-6 w-24 rounded-full" />;
  }

  const widths = ["w-16", "w-20", "w-12", "w-24"] as const;
  return <Skeleton className={cn("h-4", widths[rowIndex % widths.length])} />;
};

const renderSkeletonRows = () =>
  Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => (
    <TableRow key={`portfolio-skeleton-${rowIndex.toString()}`}>
      {COLUMNS.map((column) => (
        <TableCell
          key={column.key}
          className="h-[80px] max-h-[80px] overflow-hidden whitespace-nowrap px-2 py-2 text-left align-middle"
        >
          {renderSkeletonCell(column.key, rowIndex)}
        </TableCell>
      ))}
    </TableRow>
  ));

interface SortState {
  key: string;
  direction: "asc" | "desc";
}

export const PortfolioTable = ({
  jobs,
  isLoading,
  isError,
}: {
  jobs: PortfolioMatrixJob[];
  isLoading: boolean;
  isError: boolean;
}) => {
  const [sort, setSort] = useState<SortState | null>(null);
  const navigate = useNavigate();
  const columns = COLUMNS;

  // Navigate to the job when the row is clicked, unless the click landed on an
  // interactive control (cell links, dropdown triggers, etc.) which own it.
  const handleRowClick = (
    event: React.MouseEvent<HTMLTableRowElement>,
    job: PortfolioMatrixJob,
  ) => {
    // Note: Radix menu content is portaled, but React bubbles synthetic events
    // through the React tree, so menu-item clicks still reach this handler.
    // [role^="menuitem"] covers menuitem, menuitemcheckbox, and menuitemradio.
    if (
      (event.target as HTMLElement).closest(
        'a, button, input, textarea, [role="dialog"], [role^="menuitem"]',
      )
    ) {
      return;
    }
    void navigate(`/companies/${job.companyId}/jobs/${job.id}/?t=overview`);
  };

  const toggleSort = (key: string) => {
    setSort((prev) =>
      prev?.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );
  };

  const sortedJobs = useMemo(() => {
    // Module-level COLUMNS on purpose: stable identity keeps the memo deps
    // clean, and a hidden column can't be the active sort key anyway.
    const column = sort && COLUMNS.find((c) => c.key === sort.key);
    if (!sort || !column?.sortValue) return jobs;

    const { sortValue } = column;
    const direction = sort.direction === "asc" ? 1 : -1;

    return [...jobs].sort((a, b) => {
      const av = sortValue(a);
      const bv = sortValue(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // nulls last, regardless of direction
      if (bv == null) return -1;
      const base =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv));
      return base * direction;
    });
  }, [jobs, sort]);

  const renderRows = () => {
    if (isError) {
      return <ErrorText errorMessage="error loading data" />;
    }
    if (isLoading) {
      return renderSkeletonRows();
    }
    if (sortedJobs.length === 0) {
      return (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={columns.length} className="h-44 px-8 py-8">
            <Row className="items-center gap-3">
              <Row className="h-11 w-11 shrink-0 items-center justify-center">
                <TelescopeIcon className="h-6 w-6 text-primary-600" />
              </Row>
              <Column className="gap-1">
                <Text className="text-sm font-medium text-slate-900">
                  No matching jobs
                </Text>
                <Text className="text-sm leading-6 text-slate-500">
                  Loosen up a bit bro, clear the filters or search.
                </Text>
              </Column>
            </Row>
          </TableCell>
        </TableRow>
      );
    }

    return sortedJobs.map((job) => (
      <TableRow
        key={job.id}
        onClick={(event) => {
          handleRowClick(event, job);
        }}
        className="group cursor-pointer overflow-hidden hover:bg-slate-50/80"
        style={{ height: 80 }}
      >
        {columns.map((column) => (
          <TableCell
            key={column.key}
            className={cn(
              "h-[80px] max-h-[80px] overflow-hidden whitespace-nowrap px-2 py-2 text-left align-middle",
              column.key === "job-name" && "max-w-[280px]",
            )}
          >
            {column.key === "job-name" ? (
              <JobCell job={job} />
            ) : (
              column.render(job)
            )}
          </TableCell>
        ))}
      </TableRow>
    ));
  };
  return (
    <TooltipProvider delayDuration={200}>
      <Card className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <Row className="min-w-0 overflow-x-auto">
          <table className="w-full table-auto caption-bottom text-sm">
            <TableHeader className="sticky top-0 z-30 bg-slate-50 shadow-[0_1px_0_0_rgb(226_232_240)] [&_tr]:border-b">
              <TableRow className="hover:bg-transparent">
                {columns.map((column) => {
                  const isSorted = sort?.key === column.key;
                  const label = getColumnLabel(column, jobs.length);

                  return (
                    <TableHead
                      key={column.key}
                      className={cn(
                        "h-9 whitespace-nowrap bg-slate-50 px-3 py-3 align-middle text-sm font-medium text-slate-600",
                        column.key === "job-name" && "max-w-[280px]",
                      )}
                    >
                      {column.sortValue ? (
                        <button
                          type="button"
                          onClick={() => {
                            toggleSort(column.key);
                          }}
                          className="-mx-1 inline-flex items-center gap-1 rounded px-1 hover:text-slate-700"
                        >
                          {label}
                          {isSorted ? (
                            sort.direction === "asc" ? (
                              <ArrowUpIcon className="h-3 w-3 text-slate-700" />
                            ) : (
                              <ArrowDownIcon className="h-3 w-3 text-slate-700" />
                            )
                          ) : (
                            <ArrowUpDownIcon className="h-3 w-3 text-slate-500" />
                          )}
                        </button>
                      ) : (
                        label
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>{renderRows()}</TableBody>
          </table>
        </Row>
      </Card>
    </TooltipProvider>
  );
};
